import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  requestGetScheduledEbilling,
  useEbillingDispatch,
  useEbillingState,
} from "../../Context";
import DetailSkeleton from "../../Components/DetailSkeleton";
import { NumericFormat, PatternFormat } from "react-number-format";
import { Alert } from "../../Components/Alert";
import Badge from "../../Components/Badge";
import {
  billingPeriods,
  ebillingYearOptions,
  periodOptions,
} from "../../Constants";
import dayjs from "dayjs";
import TableSkeleton from "../../Components/TableSkeleton";
import { EmptyList } from "../../Components/EmptyList";
import { getUrl } from "aws-amplify/storage";

const EbillingScheduleDetail = () => {
  const { id } = useParams();

  const { t } = useTranslation();

  const dispatch = useEbillingDispatch();
  const { ebillingSchedule, scheduleLoading, scheduleError } =
    useEbillingState();

  useEffect(() => {
    if (id) {
      requestGetScheduledEbilling(dispatch, { id });
    }
  }, [id, dispatch]);

  const downloadFile = async (key) => {
    const urlResult = await getUrl({
      path: ({ identityId }) => `private/${identityId}/${key}`,
      options: {
        useAccelerateEndpoint: true,
      },
    });
    const signedURL = urlResult.url.href;
    window.open(signedURL);
    try {
    } catch (error) {}
  };

  const getLabelByValue = (options, value) =>
    options.find((option) => option.value === value)?.label;

  const createPeriodLabel = getLabelByValue(
    periodOptions,
    ebillingSchedule.createPeriod
  );
  const ebillingPeriodLabel = getLabelByValue(
    billingPeriods,
    ebillingSchedule.ebillingPeriod
  );
  const ebillingYearLabel = getLabelByValue(
    ebillingYearOptions,
    ebillingSchedule.ebillingYear
  );

  const ebillings = ebillingSchedule?.ebillings?.items ?? [];
  const hasFailedBilling = ebillings.some(
    (ebilling) => ebilling.status === "FAILED"
  );

  if (scheduleError) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={scheduleError?.message}
          errors={scheduleError?.errors}
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-4">
      <div className="bg-white rounded-lg lg:col-start-1 lg:col-span-2 overflow-hidden z-10">
        <div className="px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Tax Account Code (KAP)")}
              </dt>
              {scheduleLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm text-gray-900">
                  {ebillingSchedule.ebilling?.kapID}
                </dd>
              )}
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Tax Payment Code (KJS)")}
              </dt>
              {scheduleLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm text-gray-900">
                  {ebillingSchedule.ebilling?.summary?.kjs}
                </dd>
              )}
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Period")}
              </dt>
              {scheduleLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm text-gray-900">
                  {t(ebillingPeriodLabel)} / {t(ebillingYearLabel)}
                </dd>
              )}
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Tax Identification Number (NPWP)")}
              </dt>
              {scheduleLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm text-gray-900">
                  {ebillingSchedule.ebilling?.summary?.npwp && (
                    <NumericFormat
                      value={ebillingSchedule.ebilling.summary.npwp}
                      displayType={"text"}
                    />
                  )}
                </dd>
              )}
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Total")}
              </dt>
              {scheduleLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm text-gray-900">
                  {ebillingSchedule.ebilling?.total && (
                    <NumericFormat
                      displayType={"text"}
                      value={ebillingSchedule.ebilling.total}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix={"Rp"}
                    />
                  )}
                </dd>
              )}
            </div>
            {ebillingSchedule.ebilling?.collectorNpwp && (
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Tax Subject")}
                </dt>
                {scheduleLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {ebillingSchedule.ebilling.collectorNpwp ? (
                      <NumericFormat
                        value={ebillingSchedule.ebilling.collectorNpwp}
                        displayType={"text"}
                      />
                    ) : (
                      "-"
                    )}
                  </dd>
                )}
              </div>
            )}
            {ebillingSchedule.ebilling?.skNumber && (
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Notice (SK) Number")}
                </dt>
                {scheduleLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {ebillingSchedule.ebilling.sk ? (
                      <PatternFormat
                        value={ebillingSchedule.ebilling.sk}
                        displayType={"text"}
                        format="#####/###/##/###/##"
                        mask="_"
                      />
                    ) : (
                      "-"
                    )}
                  </dd>
                )}
              </div>
            )}
            {ebillingSchedule.ebilling?.nop && (
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Tax Object Number (NOP)")}
                </dt>
                {scheduleLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {ebillingSchedule.ebilling.nop ? (
                      <PatternFormat
                        value={ebillingSchedule.ebilling.nop}
                        displayType={"text"}
                        format="##.##.###.###.###.####.#"
                        mask="_"
                      />
                    ) : (
                      "-"
                    )}
                  </dd>
                )}
              </div>
            )}
          </dl>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Status")}
              </dt>
              {scheduleLoading ? (
                <DetailSkeleton />
              ) : (
                <div>
                  {ebillingSchedule.status === "ACTIVE" ? (
                    <Badge title="Active" type="success" />
                  ) : null}
                  {ebillingSchedule.status === "PAUSED" ? (
                    <Badge title="paused" type="secondary" />
                  ) : null}
                </div>
              )}
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("dateCreation")}
              </dt>
              {scheduleLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm text-gray-900">
                  {t(createPeriodLabel)}
                </dd>
              )}
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">{t("Date")}</dt>
              {scheduleLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm text-gray-900">
                  {ebillingSchedule.date}
                </dd>
              )}
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("Expired")}
              </dt>
              {scheduleLoading ? (
                <DetailSkeleton />
              ) : (
                <dd className="mt-1 text-sm text-gray-900">
                  {ebillingSchedule?.expiredAt ? (
                    dayjs(ebillingSchedule.expiredAt).format("DD-MMM-YYYY")
                  ) : (
                    <span className="mt-1 text-sm text-gray-900">-</span>
                  )}
                </dd>
              )}
            </div>
          </dl>
        </div>
      </div>
      <div className="lg:col-start-3 lg:col-span-2">
        <div className="flex flex-col">
          <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("ID")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Period")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Billing ID")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Payment")}
                      </th>
                      {hasFailedBilling ? (
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("Error")}
                        </th>
                      ) : (
                        <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                          {t("Tax Receipt")}
                        </th>
                      )}
                    </tr>
                  </thead>

                  {scheduleLoading ? (
                    <TableSkeleton rows={3} columns={5} />
                  ) : (
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {ebillings.length > 0 ? (
                        ebillings.map((ebilling) => {
                          const shortId = ebilling.id.substr(
                            ebilling.id.length - 5
                          );
                          return (
                            <tr key={ebilling.id}>
                              <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap uppercase">
                                <a href={`/ebilling/${ebilling.id}`}>
                                  {shortId}
                                </a>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                                {ebilling.startPeriod}-{ebilling.endPeriod} /{" "}
                                {ebilling.year}
                              </td>
                              <td className="px-4 py-2 text-sm whitespace-nowrap">
                                {ebilling.billingId ? (
                                  <span className="text-gray-700">
                                    {ebilling.billingId}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">
                                    {ebilling.status === "PENDING"
                                      ? t("On Process")
                                      : t("Not Available")}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm whitespace-nowrap">
                                {ebilling.billingId ? (
                                  <span className="text-gray-700">
                                    {ebilling.transactionID ? (
                                      <a
                                        href={`/transaction/${ebilling.transactionID}`}
                                      >
                                        {ebilling.transactionID.substr(
                                          ebilling.transactionID.length - 5
                                        )}
                                      </a>
                                    ) : (
                                      t("Unpaid")
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">
                                    {t("Not Available")}
                                  </span>
                                )}
                              </td>
                              {ebilling.status === "FAILED" ? (
                                <td className="px-4 py-2 text-sm text-gray-700 whitespace-normal">
                                  {ebilling.error}
                                </td>
                              ) : (
                                <td className="px-4 py-2 text-sm whitespace-nowrap">
                                  {ebilling.output?.bpn?.key ? (
                                    <button
                                      onClick={() =>
                                        downloadFile(ebilling.output.bpn.key)
                                      }
                                    >
                                      <span className="text-blue-600 underline">
                                        {t("Download")}
                                      </span>
                                    </button>
                                  ) : (
                                    <span className="text-gray-400">
                                      {ebilling.status === "SUCCESS"
                                        ? t("On Process")
                                        : t("Not Available")}
                                    </span>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td className="p-4" colSpan={5}>
                            <EmptyList title="No E-Billing" />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  )}
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default EbillingScheduleDetail;
